import {
  Fragment,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import {
  Activity,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Columns3,
  ExternalLink,
  Loader2,
  Paperclip,
  X,
} from "lucide-react";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { useIsMobile } from "@/lib/use-media-query";
import { useCustomizationLocked } from "@/lib/use-customization-locked";
import { usePermissao } from "@/lib/use-permissao";
import { STATUS_META, formatDate } from "@/components/prosthesis/prosthesis-meta";
import { ColorSelect } from "./ColorSelect";
import { NovoEstagioDialog } from "./NovoEstagioDialog";
import { ProductionFinalizeButton, ProductionStatusBadge } from "./ProductionStatusControl";
import { CelulaDeMaterial } from "./CelulaDeMaterial";
import { useResumoDeMaterial } from "./use-pendencia-material";
import {
  makeComparator,
  SORT_FIELD_LABELS,
  SORT_FIELDS,
  SORT_LEVEL_LABELS,
  type SortDirection,
  type SortField,
} from "./production-sort";
import {
  downloadAttachment,
  useProductionMutations,
  type ActiveMember,
  type Stage,
} from "./use-production-mutations";
import { MobilePlanilhaView } from "./mobile/MobilePlanilhaView";
import { DataComAtraso, PrazoComAtraso } from "./PrazoComAtraso";
import { ColunasDialog } from "./ColunasDialog";
import {
  COLUMN_KEYS,
  COLUNAS_FIXAS,
  carregarOcultas,
  carregarOrdem,
  colunasVisiveis,
  ehPadrao,
  fixarExtremos,
  salvarOcultas,
  salvarOrdem,
  type ColumnKey,
} from "./production-columns";
import {
  orderColorsSummary,
  orderMaterialsSummary,
  type IncomingOrderRow,
  type ServiceRow,
} from "./production-types";
import { rotuloDoMaterialDoServico } from "@/components/prosthesis/rotulo-do-material";

const LS_COLUMN_WIDTHS = "bookdental.producao.planilha.columnWidths";
const MIN_COLUMN_WIDTH = 28;

/** Em que largura de tela cada coluna aparece. Mora aqui e não junto de `production-columns`
 *  porque é decisão de layout desta tabela, não da preferência de quem opera — e é a mesma classe
 *  que o `<th>` e o `<td>` correspondentes já usavam. */
const CLASSE_DA_COL: Record<ColumnKey, string | undefined> = {
  select: undefined,
  status: undefined,
  modelo: undefined,
  estagio: "max-md:table-column hidden lg:table-column",
  data: "max-md:table-column hidden md:table-column",
  prazo: "max-md:table-column hidden md:table-column",
  servico: undefined,
  responsavel: undefined,
  clinica: "max-md:table-column hidden lg:table-column",
  dentista: "max-md:table-column hidden lg:table-column",
  paciente: undefined,
  dentes: "max-md:table-column hidden lg:table-column",
  material: "max-md:table-column hidden lg:table-column",
  materiais: "max-md:table-column hidden lg:table-column",
  cor: "max-md:table-column hidden lg:table-column",
  obs: "hidden xl:table-column",
  anexo: undefined,
  pedido: undefined,
  finalizar: undefined,
};


const DEFAULT_COLUMN_WIDTHS: Record<ColumnKey, number> = {
  select: 36,
  status: 48,
  modelo: 110,
  estagio: 110,
  data: 76,
  prazo: 76,
  servico: 120,
  responsavel: 120,
  clinica: 100,
  dentista: 100,
  paciente: 110,
  dentes: 64,
  material: 90,
  materiais: 84,
  cor: 64,
  obs: 90,
  anexo: 48,
  pedido: 48,
  finalizar: 60,
};

/** Larguras de coluna são por navegador (localStorage), não por conta — cada usuário ajusta a
 *  planilha do jeito que preferir. Mescla com os defaults pra cobrir coluna nova adicionada depois
 *  que o usuário já tinha salvo uma largura customizada. */
function loadColumnWidths(): Record<ColumnKey, number> {
  if (typeof window === "undefined") return DEFAULT_COLUMN_WIDTHS;
  try {
    const stored = JSON.parse(window.localStorage.getItem(LS_COLUMN_WIDTHS) ?? "{}");
    const widths = { ...DEFAULT_COLUMN_WIDTHS };
    for (const key of COLUMN_KEYS) {
      const value = stored[key];
      if (typeof value === "number" && value >= MIN_COLUMN_WIDTH) widths[key] = value;
    }
    return widths;
  } catch {
    return DEFAULT_COLUMN_WIDTHS;
  }
}

/**
 * Cabeçalho de coluna: arrasta pra reordenar, arrasta a borda pra redimensionar, clica pra ordenar.
 *
 * Três gestos no mesmo elemento, e eles não se atrapalham porque cada um ocupa um espaço próprio:
 *
 * - **Redimensionar** é só a faixa de 6px na borda direita. Ela para a propagação do `pointerdown`
 *   ALÉM do `mousedown` — o dnd-kit escuta `pointerdown`, então parar só o mousedown deixaria o
 *   arraste de reordenar começar junto com o de largura.
 * - **Ordenar** é clique sem movimento. O sensor só arma o arraste depois de 6px
 *   (`activationConstraint`), então clicar continua ordenando como antes.
 * - **Reordenar** é arrastar o cabeçalho pra qualquer lado.
 *
 * `listeners` sem `attributes`: os atributos do dnd-kit trazem `role="button"` e `tabIndex`, que num
 * `<th>` quebrariam a semântica da tabela e o `aria-sort`. O caminho por teclado pra reordenar mora
 * no painel "Colunas", que também é onde se esconde coluna — coisa que arrastar não faz.
 */
function HeaderCell({
  children,
  className,
  title,
  columnKey,
  arrastavel,
  onResizeStart,
  sortField,
  sortDirection,
  onSort,
}: {
  children: ReactNode;
  className?: string;
  title?: string;
  columnKey: ColumnKey;
  arrastavel: boolean;
  onResizeStart: (e: ReactMouseEvent) => void;
  sortField?: SortField;
  /** Direção quando ESTA coluna é o critério principal; ausente = não é a coluna ordenada. */
  sortDirection?: SortDirection;
  onSort?: (field: SortField) => void;
}) {
  const ordenavel = !!sortField && !!onSort;
  const ativa = !!sortDirection;
  const { setNodeRef, listeners, transform, transition, isDragging } = useSortable({
    id: columnKey,
    disabled: !arrastavel,
  });

  return (
    <th
      ref={setNodeRef}
      {...(arrastavel ? listeners : {})}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        "relative select-none",
        ativa && "text-brand-700 dark:text-primary",
        arrastavel && "cursor-grab active:cursor-grabbing",
        isDragging && "z-20 bg-brand-50 opacity-90 dark:bg-primary/10",
        className,
      )}
      title={title}
      aria-sort={ativa ? (sortDirection === "asc" ? "ascending" : "descending") : undefined}
    >
      {ordenavel ? (
        <button
          type="button"
          onClick={() => onSort!(sortField!)}
          title={`Ordenar por ${SORT_FIELD_LABELS[sortField!]} · arraste pra mover a coluna`}
          className="flex w-full items-center gap-1 text-left transition-colors hover:text-brand-700 dark:hover:text-primary"
        >
          {/* `flex-1` deixa a seta na borda da coluna e mantém o `mx-auto` dos cabeçalhos que são
              só ícone (Status, Anexo) centralizando do mesmo jeito que antes. */}
          <span className="min-w-0 flex-1 truncate">{children}</span>
          {ativa &&
            (sortDirection === "asc" ? (
              <ArrowUp className="h-2.5 w-2.5 shrink-0" />
            ) : (
              <ArrowDown className="h-2.5 w-2.5 shrink-0" />
            ))}
        </button>
      ) : (
        children
      )}
      <span
        onMouseDown={onResizeStart}
        onPointerDown={(e) => e.stopPropagation()}
        className="absolute right-0 top-0 z-10 h-full w-1.5 cursor-col-resize hover:bg-brand-300/60 active:bg-brand-400/80"
      />
    </th>
  );
}

export function ProductionSpreadsheetView({
  rows,
  emptyMessage,
  onOpenService,
  onOpenOrder,
}: {
  rows: ServiceRow[];
  emptyMessage: string;
  onOpenService?: (row: ServiceRow) => void;
  onOpenOrder?: (order: IncomingOrderRow) => void;
}) {
  const ehCelular = useIsMobile();
  const [tabelaCompleta, setTabelaCompleta] = useState(false);
  const [groupByOrder, setGroupByOrder] = useState(false);
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<string>>(new Set());
  const [sortCriteria, setSortCriteria] = useState<SortField[]>(["none", "none", "none"]);
  const [sortDirections, setSortDirections] = useState<SortDirection[]>(["asc", "asc", "asc"]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAssigneeId, setBulkAssigneeId] = useState("");
  const [columnWidths, setColumnWidths] = useState<Record<ColumnKey, number>>(loadColumnWidths);
  const resizingRef = useRef<{ key: ColumnKey; startX: number; startWidth: number } | null>(null);
  const [ordemDasColunas, setOrdemDasColunas] = useState<ColumnKey[]>(carregarOrdem);
  const [colunasOcultas, setColunasOcultas] = useState<Set<ColumnKey>>(carregarOcultas);
  const [colunasAbertas, setColunasAbertas] = useState(false);

  // O que a tabela desenha, na ordem escolhida. Cabeçalho e linhas percorrem esta MESMA lista —
  // é o que garante que a coluna e o dado dela nunca saiam de sincronia.
  const colunas = useMemo(
    () => colunasVisiveis(ordemDasColunas, colunasOcultas),
    [ordemDasColunas, colunasOcultas],
  );

  const aplicarOrdem = (nova: ColumnKey[]) => {
    setOrdemDasColunas(nova);
    salvarOrdem(nova);
  };
  const aplicarOcultas = (novas: Set<ColumnKey>) => {
    setColunasOcultas(novas);
    salvarOcultas(novas);
  };
  const podeReordenar = (k: ColumnKey) => !COLUNAS_FIXAS.includes(k);

  // Mesmo sensor do quadro de Estágios. Os 6px de folga são o que deixa clicar-pra-ordenar conviver
  // com arrastar-pra-mover no mesmo cabeçalho: sem movimento, não vira arraste.
  const sensoresDeColuna = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const aoSoltarColuna = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const moveis = ordemDasColunas.filter(podeReordenar);
    const de = moveis.indexOf(e.active.id as ColumnKey);
    const para = moveis.indexOf(e.over.id as ColumnKey);
    if (de === -1 || para === -1) return;
    aplicarOrdem(fixarExtremos(arrayMove(moveis, de, para)));
  };

  const restaurarColunas = () => {
    aplicarOrdem([...COLUMN_KEYS]);
    aplicarOcultas(new Set());
  };

  // A tela tem duas saídas (normal e a sobreposição "Tabela completa") e o botão Colunas vive na
  // barra, que aparece nas duas — então o diálogo precisa estar montado nas duas também.
  const dialogoDeColunas = (
    <ColunasDialog
      aberto={colunasAbertas}
      ordem={ordemDasColunas}
      ocultas={colunasOcultas}
      onOrdem={aplicarOrdem}
      onOcultas={aplicarOcultas}
      onRestaurar={restaurarColunas}
      onFechar={() => setColunasAbertas(false)}
    />
  );

  function handleResizeMove(e: MouseEvent) {
    const state = resizingRef.current;
    if (!state) return;
    const next = Math.max(MIN_COLUMN_WIDTH, state.startWidth + (e.clientX - state.startX));
    setColumnWidths((prev) => ({ ...prev, [state.key]: next }));
  }

  function handleResizeEnd() {
    resizingRef.current = null;
    window.removeEventListener("mousemove", handleResizeMove);
    window.removeEventListener("mouseup", handleResizeEnd);
    setColumnWidths((prev) => {
      window.localStorage.setItem(LS_COLUMN_WIDTHS, JSON.stringify(prev));
      return prev;
    });
  }

  function startResize(key: ColumnKey) {
    return (e: ReactMouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      resizingRef.current = { key, startX: e.clientX, startWidth: columnWidths[key] };
      window.addEventListener("mousemove", handleResizeMove);
      window.addEventListener("mouseup", handleResizeEnd);
    };
  }

  const { utils, setStatus, setAssignedTo, setModelStage, setProductionStage, stages, modelStages, activeMembers } =
    useProductionMutations();

  const comparator = useMemo(() => makeComparator(sortCriteria, sortDirections), [sortCriteria, sortDirections]);

  const sortedRows = useMemo(() => {
    if (sortCriteria.every((c) => c === "none")) return rows;
    return [...rows].sort(comparator);
  }, [rows, sortCriteria, comparator]);

  const orderGroups = useMemo(() => {
    const map = new Map<string, { order: IncomingOrderRow; rows: ServiceRow[] }>();
    for (const row of sortedRows) {
      if (!map.has(row.order.id)) map.set(row.order.id, { order: row.order, rows: [] });
      map.get(row.order.id)!.rows.push(row);
    }
    const groups = [...map.values()];
    if (sortCriteria.every((c) => c === "none")) return groups;
    // Agrupado, o pedido é representado pelo primeiro serviço já ordenado dele — critério de
    // serviço (cor, dentes) não tem valor único por pedido, então ordenar grupo por ele só faz
    // sentido pela linha que ficou na frente.
    return groups.sort((a, b) => comparator(a.rows[0], b.rows[0]));
  }, [sortedRows, sortCriteria, comparator]);

  const selectableIds = useMemo(
    () => rows.filter((r) => r.service).map((r) => r.service!.id),
    [rows],
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  const toggleExpanded = (orderId: string) => {
    setExpandedOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  function updateSortLevel(level: number, field: SortField) {
    setSortCriteria((prev) => {
      const next = [...prev];
      next[level] = field;
      return next;
    });
  }

  function toggleSortDirection(level: number) {
    setSortDirections((prev) => {
      const next = [...prev];
      next[level] = next[level] === "asc" ? "desc" : "asc";
      return next;
    });
  }

  /** Clique no título da coluna: assume o critério principal (1º nível) da ordenação que já
   *  existe — os níveis 2 e 3 continuam valendo como desempate. Clicar na coluna que já é a
   *  principal só inverte a direção. Pra limpar, "Nenhum" na barra de ordenação. */
  function ordenarPorColuna(field: SortField) {
    if (sortCriteria[0] === field) {
      toggleSortDirection(0);
      return;
    }
    updateSortLevel(0, field);
    setSortDirections((prev) => {
      const next = [...prev];
      next[0] = "asc";
      return next;
    });
  }

  const direcaoDaColuna = (field: SortField): SortDirection | undefined =>
    sortCriteria[0] === field ? sortDirections[0] : undefined;

  function toggleSelect(serviceId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(serviceId)) next.delete(serviceId);
      else next.add(serviceId);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelectedIds(allSelected ? new Set() : new Set(selectableIds));
  }

  async function applyBulkAssignee() {
    if (!bulkAssigneeId || selectedIds.size === 0) return;
    const assignedToUserId = bulkAssigneeId === "__none__" ? null : bulkAssigneeId;
    try {
      await Promise.all(
        [...selectedIds].map((serviceId) => setAssignedTo.mutateAsync({ serviceId, assignedToUserId })),
      );
      toast.success(`Responsável atualizado em ${selectedIds.size} serviço(s).`);
      setSelectedIds(new Set());
      setBulkAssigneeId("");
    } catch (e: any) {
      toast.error(e.message || "Erro ao atualizar responsável em massa.");
    }
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-sm font-medium text-zinc-900 dark:text-foreground">{emptyMessage}</p>
        <p className="mt-1 text-xs text-zinc-500 dark:text-muted-foreground">
          Pedidos de clínicas conectadas aparecem aqui automaticamente.
        </p>
      </div>
    );
  }

  // No celular a Planilha vira lista operacional (MobilePlanilhaView) — uma tabela de ~15 colunas
  // em 360px perde cabeçalho e identidade da linha por mais que a largura seja ajustada. A tabela
  // de verdade continua acessível como opção secundária, em tela cheia ("Tabela completa").
  if (ehCelular && !tabelaCompleta) {
    return (
      <MobilePlanilhaView
        rows={rows}
        onVerServico={onOpenService}
        onVerPedido={onOpenOrder}
        onAbrirTabelaCompleta={() => setTabelaCompleta(true)}
      />
    );
  }

  /**
   * O cabeçalho, indexado por coluna em vez de escrito em sequência.
   *
   * Era uma fila de 18 `<HeaderCell>` fixos — e fila não reordena. Cada célula continua idêntica ao
   * que era; o que muda é que agora existe uma chave pra apontar. O `<tr>` percorre a ordem
   * configurada, e coluna escondida simplesmente não entra na volta.
   */
  const cabecalhos: Record<ColumnKey, ReactNode> = {
    select: (
      <HeaderCell
        columnKey="select"
        arrastavel={podeReordenar("select")}
        className="px-2 py-1.5 text-left font-semibold max-md:sticky max-md:left-0 max-md:z-30 max-md:bg-zinc-50 dark:max-md:bg-background"
        onResizeStart={startResize("select")}
      >
        <label className="-m-[15px] flex h-11 w-11 cursor-pointer items-center justify-center md:m-0 md:h-auto md:w-auto">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleSelectAll}
            className="h-3.5 w-3.5 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-border"
            aria-label="Selecionar todos"
          />
        </label>
      </HeaderCell>
    ),
    status: (
      <HeaderCell
        columnKey="status"
        arrastavel={podeReordenar("status")}
        className="px-2 py-1.5 text-center font-semibold"
        title="Status"
        onResizeStart={startResize("status")}
        sortField="status"
        sortDirection={direcaoDaColuna("status")}
        onSort={ordenarPorColuna}
      >
        <Activity className="mx-auto h-3.5 w-3.5" />
      </HeaderCell>
    ),
    modelo: (
      <HeaderCell
        columnKey="modelo"
        arrastavel={podeReordenar("modelo")}
        className="truncate px-2 py-1.5 text-left font-semibold"
        title="Status do Modelo"
        onResizeStart={startResize("modelo")}
        sortField="modelStage"
        sortDirection={direcaoDaColuna("modelStage")}
        onSort={ordenarPorColuna}
      >
        Modelo
      </HeaderCell>
    ),
    estagio: (
      <HeaderCell
        columnKey="estagio"
        arrastavel={podeReordenar("estagio")}
        className="max-md:table-cell hidden truncate px-2 py-1.5 text-left font-semibold lg:table-cell"
        title="Estágio interno"
        onResizeStart={startResize("estagio")}
        sortField="stage"
        sortDirection={direcaoDaColuna("stage")}
        onSort={ordenarPorColuna}
      >
        Estágio
      </HeaderCell>
    ),
    data: (
      <HeaderCell
        columnKey="data"
        arrastavel={podeReordenar("data")}
        className="max-md:table-cell hidden truncate px-2 py-1.5 text-left font-semibold md:table-cell"
        title="Data do Pedido"
        onResizeStart={startResize("data")}
        sortField="createdAt"
        sortDirection={direcaoDaColuna("createdAt")}
        onSort={ordenarPorColuna}
      >
        Data
      </HeaderCell>
    ),
    prazo: (
      <HeaderCell
        columnKey="prazo"
        arrastavel={podeReordenar("prazo")}
        className="max-md:table-cell hidden truncate px-2 py-1.5 text-left font-semibold md:table-cell"
        onResizeStart={startResize("prazo")}
        sortField="deadline"
        sortDirection={direcaoDaColuna("deadline")}
        onSort={ordenarPorColuna}
      >
        Prazo
      </HeaderCell>
    ),
    servico: (
      <HeaderCell
        columnKey="servico"
        arrastavel={podeReordenar("servico")}
        className="truncate px-2 py-1.5 text-left font-semibold"
        onResizeStart={startResize("servico")}
        sortField="service"
        sortDirection={direcaoDaColuna("service")}
        onSort={ordenarPorColuna}
      >
        Serviço
      </HeaderCell>
    ),
    responsavel: (
      <HeaderCell
        columnKey="responsavel"
        arrastavel={podeReordenar("responsavel")}
        className="truncate px-2 py-1.5 text-left font-semibold"
        onResizeStart={startResize("responsavel")}
        sortField="assignedTo"
        sortDirection={direcaoDaColuna("assignedTo")}
        onSort={ordenarPorColuna}
      >
        Responsável
      </HeaderCell>
    ),
    clinica: (
      <HeaderCell
        columnKey="clinica"
        arrastavel={podeReordenar("clinica")}
        className="max-md:table-cell hidden truncate px-2 py-1.5 text-left font-semibold lg:table-cell"
        onResizeStart={startResize("clinica")}
        sortField="clinic"
        sortDirection={direcaoDaColuna("clinic")}
        onSort={ordenarPorColuna}
      >
        Clínica
      </HeaderCell>
    ),
    dentista: (
      <HeaderCell
        columnKey="dentista"
        arrastavel={podeReordenar("dentista")}
        className="max-md:table-cell hidden truncate px-2 py-1.5 text-left font-semibold lg:table-cell"
        onResizeStart={startResize("dentista")}
        sortField="dentist"
        sortDirection={direcaoDaColuna("dentist")}
        onSort={ordenarPorColuna}
      >
        Dentista
      </HeaderCell>
    ),
    paciente: (
      <HeaderCell
        columnKey="paciente"
        arrastavel={podeReordenar("paciente")}
        className="truncate px-2 py-1.5 text-left font-semibold"
        onResizeStart={startResize("paciente")}
        sortField="patient"
        sortDirection={direcaoDaColuna("patient")}
        onSort={ordenarPorColuna}
      >
        Paciente
      </HeaderCell>
    ),
    dentes: (
      <HeaderCell
        columnKey="dentes"
        arrastavel={podeReordenar("dentes")}
        className="max-md:table-cell hidden truncate px-2 py-1.5 text-left font-semibold lg:table-cell"
        onResizeStart={startResize("dentes")}
        sortField="teeth"
        sortDirection={direcaoDaColuna("teeth")}
        onSort={ordenarPorColuna}
      >
        Dentes
      </HeaderCell>
    ),
    material: (
      <HeaderCell
        columnKey="material"
        arrastavel={podeReordenar("material")}
        className="max-md:table-cell hidden truncate px-2 py-1.5 text-left font-semibold lg:table-cell"
        onResizeStart={startResize("material")}
        sortField="material"
        sortDirection={direcaoDaColuna("material")}
        onSort={ordenarPorColuna}
      >
        Material
      </HeaderCell>
    ),
    materiais: (
      <HeaderCell
        columnKey="materiais"
        arrastavel={podeReordenar("materiais")}
        className="max-md:table-cell hidden truncate px-2 py-1.5 text-right font-semibold lg:table-cell"
        onResizeStart={startResize("materiais")}
      >
        Insumos
      </HeaderCell>
    ),
    cor: (
      <HeaderCell
        columnKey="cor"
        arrastavel={podeReordenar("cor")}
        className="max-md:table-cell hidden truncate px-2 py-1.5 text-left font-semibold lg:table-cell"
        onResizeStart={startResize("cor")}
        sortField="shade"
        sortDirection={direcaoDaColuna("shade")}
        onSort={ordenarPorColuna}
      >
        Cor
      </HeaderCell>
    ),
    obs: (
      <HeaderCell
        columnKey="obs"
        arrastavel={podeReordenar("obs")}
        className="max-md:table-cell hidden truncate px-2 py-1.5 text-left font-semibold xl:table-cell"
        title="Observações"
        onResizeStart={startResize("obs")}
        sortField="notes"
        sortDirection={direcaoDaColuna("notes")}
        onSort={ordenarPorColuna}
      >
        Obs.
      </HeaderCell>
    ),
    anexo: (
      <HeaderCell
        columnKey="anexo"
        arrastavel={podeReordenar("anexo")}
        className="px-2 py-1.5 text-center font-semibold"
        title="Anexo"
        onResizeStart={startResize("anexo")}
        sortField="attachment"
        sortDirection={direcaoDaColuna("attachment")}
        onSort={ordenarPorColuna}
      >
        <Paperclip className="mx-auto h-3.5 w-3.5" />
      </HeaderCell>
    ),
    pedido: (
      <HeaderCell
        columnKey="pedido"
        arrastavel={podeReordenar("pedido")}
        className="px-2 py-1.5 text-center font-semibold"
        title="Pedido"
        onResizeStart={startResize("pedido")}
      >
        <ExternalLink className="mx-auto h-3.5 w-3.5" />
      </HeaderCell>
    ),
    finalizar: (
      <HeaderCell
        columnKey="finalizar"
        arrastavel={podeReordenar("finalizar")}
        className="px-2 py-1.5 text-center font-semibold"
        title="Finalizar"
        onResizeStart={startResize("finalizar")}
      >
        <CheckCircle2 className="mx-auto h-3.5 w-3.5" />
      </HeaderCell>
    ),
  };

  const conteudo = (
    <div>
      {selectedIds.size > 0 ? (
        // Na sobreposição do celular a largura do conteúdo é a da tabela (1470px) — as barras de
        // ferramentas grudam na esquerda com largura de tela, senão ficam fora da área visível.
        <div className="flex flex-wrap items-center gap-2 border-b border-brand-100 bg-brand-50/60 px-3 py-1.5 max-md:sticky max-md:left-0 max-md:w-screen dark:border-primary/20 dark:bg-primary/5">
          <span className="text-[11px] font-semibold text-brand-700 dark:text-primary">
            {selectedIds.size} selecionado{selectedIds.size > 1 ? "s" : ""}
          </span>
          <select
            value={bulkAssigneeId}
            onChange={(e) => setBulkAssigneeId(e.target.value)}
            className="h-11 md:h-6 rounded border border-zinc-200 bg-white px-1 text-[11px] text-zinc-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
          >
            <option value="">Escolher responsável...</option>
            <option value="__none__">-</option>
            {activeMembers.map((m) => (
              <option key={m.user.id} value={m.user.id}>
                {m.user.name ?? m.user.email}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!bulkAssigneeId || setAssignedTo.isPending}
            onClick={applyBulkAssignee}
            className="inline-flex h-11 md:h-6 items-center rounded bg-brand-600 px-2 text-[11px] font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
          >
            Aplicar a todos
          </button>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="inline-flex h-11 md:h-6 items-center rounded border border-zinc-200 px-2 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground"
          >
            Cancelar
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1.5 border-b border-zinc-100 px-3 py-1.5 max-md:sticky max-md:left-0 max-md:w-screen max-md:justify-start dark:border-border">
          <div className="flex items-center gap-2 text-[11px] text-zinc-500 dark:text-muted-foreground">
            <span className="font-medium">Ordenar por:</span>
            {[0, 1, 2].map((level) => (
              <div key={level} className="flex items-center gap-1">
                <span className="font-semibold text-zinc-400 dark:text-muted-foreground/70">
                  {SORT_LEVEL_LABELS[level]}
                </span>
                <select
                  value={sortCriteria[level]}
                  onChange={(e) => updateSortLevel(level, e.target.value as SortField)}
                  className="h-11 md:h-6 rounded border border-zinc-200 bg-white px-1 text-[11px] text-zinc-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
                >
                  {SORT_FIELDS.map((f) => (
                    <option key={f} value={f}>
                      {SORT_FIELD_LABELS[f]}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={sortCriteria[level] === "none"}
                  onClick={() => toggleSortDirection(level)}
                  title={sortDirections[level] === "asc" ? "Ordem crescente" : "Ordem decrescente"}
                  aria-label={sortDirections[level] === "asc" ? "Ordem crescente" : "Ordem decrescente"}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded border border-zinc-200 text-zinc-500 transition-colors hover:bg-zinc-50 disabled:opacity-40 md:h-6 md:w-6 dark:border-border dark:text-muted-foreground dark:hover:bg-muted"
                >
                  {sortDirections[level] === "asc" ? (
                    <ArrowUp className="h-3 w-3" />
                  ) : (
                    <ArrowDown className="h-3 w-3" />
                  )}
                </button>
              </div>
            ))}
          </div>
          <label className="flex h-11 select-none items-center gap-1.5 text-[11px] font-medium text-zinc-600 md:h-auto dark:text-muted-foreground">
            <input
              type="checkbox"
              checked={groupByOrder}
              onChange={(e) => setGroupByOrder(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-border"
            />
            Agrupar por pedido
          </label>
          <button
            type="button"
            onClick={() => setColunasAbertas(true)}
            className="inline-flex h-11 items-center gap-1.5 rounded border border-zinc-200 px-2 text-[11px] font-medium text-zinc-600 transition-colors hover:bg-zinc-50 md:h-6 dark:border-border dark:text-muted-foreground dark:hover:bg-muted"
          >
            <Columns3 className="h-3 w-3" />
            Colunas
            {!ehPadrao(ordemDasColunas, colunasOcultas) && (
              <span className="h-1.5 w-1.5 rounded-full bg-brand-500" title="Arranjo personalizado" />
            )}
          </button>
        </div>
      )}
      {/* No desktop a grade rola de lado dentro da própria área. No celular ela só existe dentro
          da sobreposição "Tabela completa", cujo contêiner externo rola nos DOIS eixos — por isso
          aqui não pode haver overflow próprio abaixo de md, senão o cabeçalho fixo (sticky) da
          sobreposição deixaria de funcionar. Abaixo de md todas as colunas ficam visíveis
          (max-md:table-*): tabela completa é completa mesmo. */}
      <div data-planilha className="md:overflow-x-auto md:overscroll-x-contain">
        <DndContext sensors={sensoresDeColuna} collisionDetection={closestCenter} onDragEnd={aoSoltarColuna}>
        <table className="w-full table-fixed text-[12px] max-md:min-w-[1470px] md:text-[11px]">
          {/* O colgroup tem que percorrer a MESMA lista do cabeçalho: `<col>` casa por posição, e
              uma lista fora de ordem daria a largura de uma coluna pra outra. */}
          <colgroup>
            {colunas.map((coluna) => (
              <col key={coluna} className={CLASSE_DA_COL[coluna]} style={{ width: columnWidths[coluna] }} />
            ))}
          </colgroup>
          {/* Na sobreposição do celular o cabeçalho gruda no topo e a coluna de seleção na esquerda
              (sticky), com fundo sólido — o translúcido do desktop deixaria as células passarem por
              baixo durante a rolagem. */}
          {/* O DndContext envolve só a tabela: nenhum DOM próprio, e o arraste de coluna não
              disputa com o arraste de card do quadro de Estágios, que vive em outra tela. */}
          <thead className="border-b border-zinc-100 bg-zinc-50/50 text-[9px] uppercase tracking-wider text-zinc-500 max-md:sticky max-md:top-0 max-md:z-20 max-md:bg-zinc-50 max-md:text-[10px] dark:border-border dark:bg-background/50 dark:text-muted-foreground dark:max-md:bg-background">
            <tr>
              <SortableContext items={colunas.filter(podeReordenar)} strategy={horizontalListSortingStrategy}>
                {colunas.map((coluna) => (
                  <Fragment key={coluna}>{cabecalhos[coluna]}</Fragment>
                ))}
              </SortableContext>
            </tr>
          </thead>
          <tbody>
            {groupByOrder
              ? orderGroups.map((group) => (
                  <OrderGroup
                    key={group.order.id}
                    order={group.order}
                    rows={group.rows}
                    expanded={expandedOrderIds.has(group.order.id)}
                    onToggle={() => toggleExpanded(group.order.id)}
                    onOpenService={onOpenService}
                    onOpenOrder={onOpenOrder}
                    setStatus={setStatus}
                    setAssignedTo={setAssignedTo}
                    setModelStage={setModelStage}
                    setProductionStage={setProductionStage}
                    stages={stages}
                    modelStages={modelStages}
                    activeMembers={activeMembers}
                    utils={utils}
                    selectedIds={selectedIds}
                    onToggleSelect={toggleSelect}
                    colunas={colunas}
                  />
                ))
              : sortedRows.map((row, i) => (
                  <ServiceTableRow
                    key={row.service ? row.service.id : `${row.order.id}-empty`}
                    row={row}
                    indented={false}
                    muted={false}
                    onOpenService={onOpenService}
                    onOpenOrder={onOpenOrder}
                    setStatus={setStatus}
                    setAssignedTo={setAssignedTo}
                    setModelStage={setModelStage}
                    setProductionStage={setProductionStage}
                    stages={stages}
                    modelStages={modelStages}
                    activeMembers={activeMembers}
                    utils={utils}
                    borderTop={i > 0}
                    selected={row.service ? selectedIds.has(row.service.id) : false}
                    onToggleSelect={toggleSelect}
                    colunas={colunas}
                  />
                ))}
          </tbody>
        </table>
        </DndContext>
      </div>
    </div>
  );

  // "Tabela completa" no celular: a MESMA tabela do desktop, em tela cheia, com todas as colunas,
  // cabeçalho fixo, coluna de seleção congelada e rolagem interna nos dois eixos.
  if (ehCelular) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-card">
        <div className="flex h-12 shrink-0 items-center justify-between border-b border-zinc-200 pl-3 pr-1 dark:border-border">
          <p className="text-sm font-bold text-zinc-900 dark:text-foreground">Tabela completa</p>
          <button
            type="button"
            onClick={() => setTabelaCompleta(false)}
            aria-label="Fechar tabela completa"
            className="flex h-11 w-11 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-800 dark:text-muted-foreground dark:hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="shrink-0 border-b border-zinc-100 bg-zinc-50 px-3 py-1.5 text-[11px] text-zinc-500 dark:border-border dark:bg-background dark:text-muted-foreground">
          Deslize para os lados pra ver todas as colunas — na horizontal cabe mais.
        </p>
        <div className="min-h-0 flex-1 overflow-auto overscroll-contain">{conteudo}</div>
        {dialogoDeColunas}
      </div>
    );
  }

  return (
    <>
      {conteudo}
      {dialogoDeColunas}
    </>
  );
}

function OrderGroup({
  order,
  rows,
  expanded,
  onToggle,
  onOpenService,
  onOpenOrder,
  setStatus,
  setAssignedTo,
  setModelStage,
  setProductionStage,
  stages,
  modelStages,
  activeMembers,
  utils,
  selectedIds,
  onToggleSelect,
  colunas,
}: {
  order: IncomingOrderRow;
  rows: ServiceRow[];
  expanded: boolean;
  onToggle: () => void;
  onOpenService?: (row: ServiceRow) => void;
  onOpenOrder?: (order: IncomingOrderRow) => void;
  setStatus: ReturnType<typeof trpc.prosthesis.incoming.setStatus.useMutation>;
  setAssignedTo: ReturnType<typeof trpc.prosthesis.incoming.setAssignedTo.useMutation>;
  setModelStage: ReturnType<typeof trpc.prosthesis.incoming.setModelStage.useMutation>;
  setProductionStage: ReturnType<typeof trpc.prosthesis.incoming.setProductionStage.useMutation>;
  stages: Stage[];
  modelStages: Stage[];
  activeMembers: ActiveMember[];
  utils: ReturnType<typeof trpc.useUtils>;
  selectedIds: Set<string>;
  onToggleSelect: (serviceId: string) => void;
  colunas: ColumnKey[];
}) {
  // A linha do PEDIDO tem as mesmas colunas da linha de serviço, mas quase todas vazias: o que o
  // pedido sabe é data, prazo, clínica, dentista, paciente e o resumo de material/cor. O travessão
  // no resto não é enfeite — mantém a linha alinhada com as colunas de baixo.
  const celulasDoPedido: Record<ColumnKey, ReactNode> = {
    select: (
      <td
        className="px-2 py-1 max-md:sticky max-md:left-0 max-md:z-10 max-md:bg-white dark:max-md:bg-card"
        onClick={(e) => e.stopPropagation()}
      />
    ),
    status: (
      <td className="overflow-hidden whitespace-nowrap px-2 py-1">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className="rounded p-0.5 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-700 dark:hover:bg-white/10 dark:hover:text-foreground"
            aria-label={expanded ? "Recolher pedido" : "Expandir pedido"}
          >
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
          <span
            className="min-w-0 truncate rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase"
            style={{ backgroundColor: STATUS_META[order.status].bg, color: STATUS_META[order.status].color }}
            title={STATUS_META[order.status].label}
          >
            {STATUS_META[order.status].label}
          </span>
        </div>
      </td>
    ),
    modelo: <td className="px-2 py-1 text-center text-zinc-300 dark:text-muted-foreground/50">—</td>,
    estagio: (
      <td className="max-md:table-cell hidden px-2 py-1 text-center text-zinc-300 dark:text-muted-foreground/50 lg:table-cell">
        —
      </td>
    ),
    data: (
      <td
        className="max-md:table-cell hidden truncate whitespace-nowrap px-2 py-1 tabular-nums text-zinc-700 dark:text-foreground/80 md:table-cell"
        title={formatDate(order.createdAt)}
      >
        <DataComAtraso rows={rows} />
      </td>
    ),
    prazo: (
      <td
        className="max-md:table-cell hidden truncate whitespace-nowrap px-2 py-1 tabular-nums text-zinc-700 dark:text-foreground/80 md:table-cell"
        title={formatDate(order.deadline)}
      >
        <PrazoComAtraso rows={rows} />
      </td>
    ),
    servico: (
      <td
        className="truncate px-2 py-1 font-medium text-zinc-900 dark:text-foreground"
        title={`${rows.length} ${rows.length === 1 ? "serviço" : "serviços"}`}
      >
        {rows.length} {rows.length === 1 ? "serviço" : "serviços"}
      </td>
    ),
    responsavel: <td className="px-2 py-1 text-center text-zinc-300 dark:text-muted-foreground/50">—</td>,
    clinica: (
      <td
        className="max-md:table-cell hidden truncate px-2 py-1 text-zinc-700 dark:text-foreground/80 lg:table-cell"
        title={order.organization.isPersonal ? undefined : order.organization.name}
      >
        {order.organization.isPersonal ? "" : order.organization.name}
      </td>
    ),
    dentista: (
      <td
        className="max-md:table-cell hidden truncate px-2 py-1 text-zinc-700 dark:text-foreground/80 lg:table-cell"
        title={order.dentistName ?? undefined}
      >
        {order.dentistName ?? "—"}
      </td>
    ),
    paciente: (
      <td className="truncate px-2 py-1 text-zinc-700 dark:text-foreground/80" title={order.patientName}>
        {order.patientName}
      </td>
    ),
    dentes: (
      <td className="max-md:table-cell hidden truncate px-2 py-1 text-zinc-700 dark:text-foreground/80 lg:table-cell">
        —
      </td>
    ),
    material: (
      <td
        className="max-md:table-cell hidden truncate px-2 py-1 text-zinc-700 dark:text-foreground/80 lg:table-cell"
        title={orderMaterialsSummary(order)}
      >
        {orderMaterialsSummary(order)}
      </td>
    ),
    materiais: (
      <td className="max-md:table-cell hidden px-2 py-1 text-right text-zinc-300 lg:table-cell dark:text-muted-foreground/50">
        —
      </td>
    ),
    cor: (
      <td
        className="max-md:table-cell hidden truncate px-2 py-1 text-zinc-700 dark:text-foreground/80 lg:table-cell"
        title={orderColorsSummary(order)}
      >
        {orderColorsSummary(order)}
      </td>
    ),
    obs: (
      <td className="max-md:table-cell hidden truncate px-2 py-1 text-zinc-700 dark:text-foreground/80 xl:table-cell">
        —
      </td>
    ),
    anexo: <td className="px-2 py-1 text-center text-zinc-300 dark:text-muted-foreground/50">—</td>,
    pedido: (
      <td className="px-2 py-1 text-center">
        <OrderButton order={order} onOpenOrder={onOpenOrder} />
      </td>
    ),
    finalizar: <td className="px-2 py-1 text-center text-zinc-300 dark:text-muted-foreground/50">—</td>,
  };

  return (
    <>
      <tr
        className="cursor-pointer border-b border-zinc-100 bg-zinc-50/30 hover:bg-zinc-50 dark:border-border/50 dark:bg-background/30 dark:hover:bg-white/5"
        onClick={() => onOpenOrder?.(order)}
      >
        {colunas.map((coluna) => (
          <Fragment key={coluna}>{celulasDoPedido[coluna]}</Fragment>
        ))}
      </tr>
      {expanded &&
        rows.map((row) => (
          <ServiceTableRow
            key={row.service.id}
            row={row}
            indented
            muted
            onOpenService={onOpenService}
            onOpenOrder={onOpenOrder}
            setStatus={setStatus}
            setAssignedTo={setAssignedTo}
            setModelStage={setModelStage}
            setProductionStage={setProductionStage}
            stages={stages}
            modelStages={modelStages}
            activeMembers={activeMembers}
            utils={utils}
            borderTop={false}
            selected={selectedIds.has(row.service.id)}
            onToggleSelect={onToggleSelect}
            colunas={colunas}
          />
        ))}
    </>
  );
}

function ServiceTableRow({
  row,
  indented,
  muted,
  onOpenService,
  onOpenOrder,
  setStatus,
  setAssignedTo,
  setModelStage,
  setProductionStage,
  stages,
  modelStages,
  activeMembers,
  utils,
  borderTop,
  selected,
  onToggleSelect,
  colunas,
}: {
  row: ServiceRow;
  indented: boolean;
  muted: boolean;
  onOpenService?: (row: ServiceRow) => void;
  onOpenOrder?: (order: IncomingOrderRow) => void;
  setStatus: ReturnType<typeof trpc.prosthesis.incoming.setStatus.useMutation>;
  setAssignedTo: ReturnType<typeof trpc.prosthesis.incoming.setAssignedTo.useMutation>;
  setModelStage: ReturnType<typeof trpc.prosthesis.incoming.setModelStage.useMutation>;
  setProductionStage: ReturnType<typeof trpc.prosthesis.incoming.setProductionStage.useMutation>;
  stages: Stage[];
  modelStages: Stage[];
  activeMembers: ActiveMember[];
  utils: ReturnType<typeof trpc.useUtils>;
  borderTop: boolean;
  selected: boolean;
  onToggleSelect: (serviceId: string) => void;
  colunas: ColumnKey[];
}) {
  const podeGerenciar = usePermissao("production.manage");
  // Um mapa para a planilha inteira — vem de uma consulta só, cacheada pelo react-query.
  const { porServico: resumoDeMaterial } = useResumoDeMaterial();
  const { order, service } = row;
  const orderCellClass = muted ? "text-zinc-400 dark:text-muted-foreground/70" : "text-zinc-700 dark:text-foreground/80";
  const serviceLabel = service?.serviceCategory.label ?? "—";
  const material = rotuloDoMaterialDoServico(service) ?? "—";
  const color = service?.shadeOption?.code ?? "—";
  const teeth = service?.teeth || "—";

  const celulas: Record<ColumnKey, ReactNode> = {
    select: (
      <td
        className="px-2 py-1 max-md:sticky max-md:left-0 max-md:z-10 max-md:bg-white dark:max-md:bg-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* A seleção só existe pra atribuir responsável em lote — sem permissão de gerenciar, ela
            não leva a lugar nenhum. */}
        {service && podeGerenciar && (
          <label className="-m-[15px] flex h-11 w-11 cursor-pointer items-center justify-center md:m-0 md:h-auto md:w-auto">
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onToggleSelect(service.id)}
              className="h-3.5 w-3.5 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-border"
              aria-label={`Selecionar ${serviceLabel}`}
            />
          </label>
        )}
      </td>
    ),
    status: (
      <td className={cn("overflow-hidden whitespace-nowrap px-2 py-1 text-center", indented && "pl-7")}>
        {service && <ProductionStatusBadge status={service.status} compact />}
      </td>
    ),
    modelo: (
      <td className="overflow-hidden whitespace-nowrap px-2 py-1">
        <ModelStageSelect service={service} modelStages={modelStages} setModelStage={setModelStage} />
      </td>
    ),
    estagio: (
      <td className="max-md:table-cell hidden overflow-hidden whitespace-nowrap px-2 py-1 lg:table-cell">
        <StageSelect service={service} stages={stages} setProductionStage={setProductionStage} />
      </td>
    ),
    data: (
      <td
        className={cn("max-md:table-cell hidden truncate whitespace-nowrap px-2 py-1 tabular-nums md:table-cell", orderCellClass)}
        title={formatDate(order.createdAt)}
      >
        <DataComAtraso rows={[row]} />
      </td>
    ),
    prazo: (
      <td
        className={cn("max-md:table-cell hidden truncate whitespace-nowrap px-2 py-1 tabular-nums md:table-cell", orderCellClass)}
        title={formatDate(order.deadline)}
      >
        <PrazoComAtraso rows={[row]} />
      </td>
    ),
    servico: (
      <td className="truncate px-2 py-1 font-medium text-zinc-900 dark:text-foreground" title={serviceLabel}>
        {serviceLabel}
      </td>
    ),
    responsavel: (
      <td className="overflow-hidden whitespace-nowrap px-2 py-1">
        <AssignedToSelect service={service} activeMembers={activeMembers} setAssignedTo={setAssignedTo} />
      </td>
    ),
    clinica: (
      <td
        className={cn("max-md:table-cell hidden truncate px-2 py-1 lg:table-cell", orderCellClass)}
        title={order.organization.isPersonal ? undefined : order.organization.name}
      >
        {order.organization.isPersonal ? "" : order.organization.name}
      </td>
    ),
    dentista: (
      <td className={cn("max-md:table-cell hidden truncate px-2 py-1 lg:table-cell", orderCellClass)} title={order.dentistName ?? undefined}>
        {order.dentistName ?? "—"}
      </td>
    ),
    paciente: (
      <td className={cn("truncate px-2 py-1", orderCellClass)} title={order.patientName}>
        {order.patientName}
      </td>
    ),
    dentes: (
      <td
        className="max-md:table-cell hidden truncate px-2 py-1 text-zinc-700 dark:text-foreground/80 lg:table-cell"
        title={teeth}
      >
        {teeth}
      </td>
    ),
    material: (
      <td
        className="max-md:table-cell hidden truncate px-2 py-1 text-zinc-700 dark:text-foreground/80 lg:table-cell"
        title={material}
      >
        {material}
      </td>
    ),
    materiais: (
      <td className="max-md:table-cell hidden px-2 py-1 text-right lg:table-cell">
        <CelulaDeMaterial resumo={service ? resumoDeMaterial.get(service.id) : undefined} />
      </td>
    ),
    cor: (
      <td
        className="max-md:table-cell hidden truncate px-2 py-1 text-zinc-700 dark:text-foreground/80 lg:table-cell"
        title={color}
      >
        {color}
      </td>
    ),
    obs: (
      <td
        className="max-md:table-cell hidden truncate px-2 py-1 text-zinc-700 dark:text-foreground/80 xl:table-cell"
        title={service?.notes ?? undefined}
      >
        {service?.notes || "—"}
      </td>
    ),
    anexo: (
      <td className="px-2 py-1 text-center">
        {service?.attachmentFile ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              downloadAttachment(utils, service.attachmentFile!.id);
            }}
            className="text-brand-500 hover:text-brand-800 dark:hover:text-primary"
            aria-label="Baixar arquivo anexado"
            title="Baixar arquivo anexado"
          >
            <Paperclip className="h-3.5 w-3.5" />
          </button>
        ) : (
          <span className="text-zinc-300 dark:text-muted-foreground/50">—</span>
        )}
      </td>
    ),
    pedido: (
      <td className="px-2 py-1 text-center">
        <OrderButton order={order} onOpenOrder={onOpenOrder} />
      </td>
    ),
    finalizar: (
      <td className="px-2 py-1 text-center" onClick={(e) => e.stopPropagation()}>
        {service && podeGerenciar && (
          <ProductionFinalizeButton
            status={service.status}
            disabled={setStatus.isPending}
            serviceId={service.id}
            onConclude={() => setStatus.mutate({ serviceId: service.id, status: "CONCLUIDO" })}
            onCancel={() => setStatus.mutate({ serviceId: service.id, status: "CANCELADO" })}
          />
        )}
      </td>
    ),
  };

  return (
    <tr
      className={cn(
        "cursor-pointer border-zinc-100 hover:bg-zinc-50 dark:border-border/50 dark:hover:bg-white/5",
        borderTop && "border-t",
        !borderTop && "border-b",
        selected && "bg-brand-50/60 dark:bg-primary/5",
      )}
      onClick={() => onOpenService?.(row)}
    >
      {colunas.map((coluna) => (
        <Fragment key={coluna}>{celulas[coluna]}</Fragment>
      ))}
    </tr>
  );
}

function OrderButton({
  order,
  onOpenOrder,
}: {
  order: IncomingOrderRow;
  onOpenOrder?: (order: IncomingOrderRow) => void;
}) {
  if (!onOpenOrder) return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onOpenOrder(order);
      }}
      className="-m-3 inline-flex h-11 w-11 items-center justify-center text-zinc-500 hover:text-brand-700 md:m-0 md:h-auto md:w-auto dark:text-muted-foreground dark:hover:text-primary"
      aria-label="Ver pedido completo"
      title="Ver pedido completo"
    >
      <ExternalLink className="h-3.5 w-3.5" />
    </button>
  );
}

/** Etiqueta só-leitura de estágio — o que fica no lugar do seletor pra quem só tem
 *  `production.view`. Mesmas cores da opção escolhida, sem afordância de clique. */
function EtiquetaDeEstagio({ rotulo, cor }: { rotulo: string; cor?: string | null }) {
  const base = cor ?? "#71717a";
  return (
    <span
      className="inline-flex max-w-full truncate rounded px-1.5 py-0.5 text-[10px] font-semibold"
      style={{ backgroundColor: `${base}1a`, color: base }}
      title={rotulo}
    >
      {rotulo}
    </span>
  );
}

function AssignedToSelect({
  service,
  activeMembers,
  setAssignedTo,
}: {
  service: ServiceRow["service"];
  activeMembers: ActiveMember[];
  setAssignedTo: ReturnType<typeof trpc.prosthesis.incoming.setAssignedTo.useMutation>;
}) {
  const podeGerenciar = usePermissao("production.manage");
  if (!service) return null;
  if (!podeGerenciar) {
    return (
      <span className="block truncate text-[10px] font-medium text-zinc-700 dark:text-foreground/80">
        {service.assignedToUser?.name ?? "—"}
      </span>
    );
  }
  return (
    <select
      value={service.assignedToUserId ?? ""}
      disabled={setAssignedTo.isPending}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setAssignedTo.mutate({ serviceId: service.id, assignedToUserId: e.target.value || null })}
      className="max-w-full truncate rounded border border-zinc-200 bg-white px-1 py-0.5 text-[10px] font-medium text-zinc-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
      title={service.assignedToUser?.name ?? "-"}
    >
      <option value="">-</option>
      {activeMembers.map((m) => (
        <option key={m.user.id} value={m.user.id}>
          {m.user.name ?? m.user.email}
        </option>
      ))}
    </select>
  );
}

const NO_MODEL_STAGE = "__none__";

function ModelStageSelect({
  service,
  modelStages,
  setModelStage,
}: {
  service: ServiceRow["service"];
  modelStages: Stage[];
  setModelStage: ReturnType<typeof trpc.prosthesis.incoming.setModelStage.useMutation>;
}) {
  const podeGerenciar = usePermissao("production.manage");
  const [criando, setCriando] = useState(false);
  const travado = useCustomizationLocked();
  if (!service) return null;
  if (!podeGerenciar) {
    const atual = modelStages.find((s) => s.id === service.modelStageId);
    return <EtiquetaDeEstagio rotulo={atual?.label ?? "Não possui"} cor={atual?.colorHex} />;
  }
  return (
    <>
      <ColorSelect
        value={service.modelStageId ?? NO_MODEL_STAGE}
        disabled={setModelStage.isPending}
        onClick={(e) => e.stopPropagation()}
        onChange={(v) =>
          setModelStage.mutate({ serviceId: service.id, modelStageId: v === NO_MODEL_STAGE ? null : v })
        }
        className="h-11 w-full md:h-auto"
        options={[
          { value: NO_MODEL_STAGE, label: "Não possui", bg: "#f4f4f5", color: "#71717a" },
          ...modelStages.map((s) => ({
            value: s.id,
            label: s.label,
            bg: `${s.colorHex ?? "#78716c"}1a`,
            color: s.colorHex ?? "#78716c",
          })),
        ]}
        acaoRodape={travado ? undefined : { label: "Nova etapa do modelo", onClick: () => setCriando(true) }}
      />
      <NovoEstagioDialog
        aberto={criando}
        modelo
        onFechar={() => setCriando(false)}
        onCriado={(etapa) => setModelStage.mutate({ serviceId: service.id, modelStageId: etapa.id })}
      />
    </>
  );
}

const NO_STAGE = "__none__";

function StageSelect({
  service,
  stages,
  setProductionStage,
}: {
  service: ServiceRow["service"];
  stages: Stage[];
  setProductionStage: ReturnType<typeof trpc.prosthesis.incoming.setProductionStage.useMutation>;
}) {
  // Criar estágio faz parte da personalização de produção — o servidor já recusa sem o plano,
  // então aqui o atalho simplesmente não aparece.
  const travado = useCustomizationLocked();
  const podeGerenciar = usePermissao("production.manage");
  const [criando, setCriando] = useState(false);

  if (!service) return null;
  if (!podeGerenciar) {
    const atual = stages.find((s) => s.id === service.productionStageId);
    return <EtiquetaDeEstagio rotulo={atual?.label ?? "Sem estágio"} cor={atual?.colorHex} />;
  }
  return (
    <>
      <ColorSelect
        value={service.productionStageId ?? NO_STAGE}
        disabled={setProductionStage.isPending}
        onClick={(e) => e.stopPropagation()}
        onChange={(v) =>
          setProductionStage.mutate({ serviceId: service.id, productionStageId: v === NO_STAGE ? null : v })
        }
        className="h-11 w-full md:h-auto"
        options={[
          { value: NO_STAGE, label: "Sem estágio", bg: "#f4f4f5", color: "#71717a" },
          ...stages.map((s) => ({
            value: s.id,
            label: s.label,
            bg: `${s.colorHex ?? "#2457ff"}1a`,
            color: s.colorHex ?? "#2457ff",
          })),
        ]}
        acaoRodape={travado ? undefined : { label: "Novo estágio", onClick: () => setCriando(true) }}
      />
      <NovoEstagioDialog
        aberto={criando}
        onFechar={() => setCriando(false)}
        onCriado={(estagio) => setProductionStage.mutate({ serviceId: service.id, productionStageId: estagio.id })}
      />
    </>
  );
}

export function ProductionLoading() {
  return (
    <div className="flex justify-center py-16">
      <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
    </div>
  );
}
